import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight, Download } from "lucide-react";
import { useI18n } from "@/i18n";
import { scrollToSection } from "@/components/Header";
import { cvUrl } from "@/lib/api";
import { EASE, Cross } from "@/components/Reveal";

const HERO_IMG = "https://images.pexels.com/photos/32529354/pexels-photo-32529354.jpeg?auto=compress&cs=tinysrgb&w=1200";

const lineAnim = (i) => ({
  initial: { y: "115%" },
  animate: { y: "0%" },
  transition: { duration: 1.1, ease: EASE, delay: 0.25 + i * 0.13 },
});

export default function Hero() {
  const { t } = useI18n();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const ySchematic = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <section ref={ref} className="relative flex min-h-screen flex-col justify-center overflow-hidden px-6 pb-16 pt-28 md:px-10 xl:px-16" data-testid="hero-section">
      <motion.div aria-hidden="true" style={{ y: ySchematic }} className="pointer-events-none absolute -left-10 top-24 hidden opacity-[0.07] lg:block">
        <svg width="560" height="640" viewBox="0 0 560 640" fill="none">
          <path d="M40 0v640M120 0v640M200 0v640M280 0v640M360 0v640" stroke="#fff" strokeWidth="1" />
          <path d="M0 80h560M0 160h560M0 240h560M0 320h560M0 400h560M0 480h560" stroke="#fff" strokeWidth="1" />
          {[80, 160, 240, 320, 400, 480].map((y) =>
            [40, 120, 200, 280, 360].map((x) => <circle key={`${x}-${y}`} cx={x} cy={y} r="3" fill="#050505" stroke="#fff" />)
          )}
        </svg>
      </motion.div>

      <Cross className="left-6 top-24 hidden md:block" />
      <Cross className="bottom-10 left-10 hidden md:block" />

      <div className="relative mx-auto grid w-full max-w-[1440px] items-center gap-14 lg:grid-cols-12">
        <div className="relative z-10 lg:col-span-7">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.1 }} className="mb-8 flex items-center gap-3 text-[11px] tracking-[0.3em] text-zinc-400" data-testid="hero-eyebrow">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-gold opacity-60" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-gold" />
            </span>
            {t.hero.eyebrow}
          </motion.div>

          <h1 className="font-display font-extrabold leading-[0.95] text-white">
            {t.hero.lines.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block text-[clamp(2.4rem,6.6vw,5.4rem)] ${i === 1 ? "text-gold" : ""}`}
                  {...lineAnim(i)}
                  data-testid={`hero-line-${i}`}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE, delay: 0.8 }} className="mt-8 max-w-xl text-sm leading-relaxed text-zinc-400 md:text-base" data-testid="hero-sub">
            {t.hero.sub}
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE, delay: 0.95 }} className="mt-10 flex flex-wrap items-center gap-4">
            <button
              data-testid="hero-cta-projects"
              onClick={() => scrollToSection("#projects")}
              className="group flex items-center gap-3 bg-gold px-6 py-3.5 text-xs font-bold tracking-[0.2em] text-black transition-transform duration-200 hover:-translate-y-0.5"
            >
              {t.hero.ctaProjects}
              <ArrowRight size={14} className="transition-transform duration-200 group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-cta-contact"
              onClick={() => scrollToSection("#contact")}
              className="flex items-center gap-3 border border-line px-6 py-3.5 text-xs font-bold tracking-[0.2em] text-white transition-colors duration-200 hover:border-gold hover:text-gold"
            >
              {t.hero.ctaContact}
            </button>
            <a
              href={cvUrl()}
              download="CALEB_DEMISEW_ZELEKE_CV.pdf"
              target="_blank"
              rel="noreferrer"
              data-testid="hero-cv-btn"
              className="group flex items-center gap-3 border border-gold/40 px-6 py-3.5 text-xs font-bold tracking-[0.2em] text-gold transition-colors duration-200 hover:bg-gold hover:text-black"
            >
              {t.hero.cvBtn}
              <Download size={14} className="transition-transform duration-200 group-hover:translate-y-0.5" />
            </a>
          </motion.div>
        </div>

        <div className="relative lg:col-span-5">
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1, ease: EASE, delay: 0.5 }} className="relative" data-testid="hero-visual">
            <div className="relative overflow-hidden border border-line">
              <motion.img
                src={HERO_IMG}
                alt="Industrial control cabinet"
                style={{ y: yImg }}
                className="h-[340px] w-full scale-110 object-cover md:h-[440px]"
                data-testid="hero-image"
              />
              <div className="scanline" />
              <span className="absolute left-3 top-3 text-[10px] tracking-[0.25em] text-gold">REC // 24V DC</span>
            </div>
            <div className="flex items-center justify-end pt-2 text-[10px] tracking-[0.2em] text-zinc-500">
              <span className="text-gold">[ 24-230V ]</span>
            </div>
            <span className="absolute bottom-3 right-3 z-10 text-[10px] tracking-[0.25em] text-gold/80">{t.hero.fig}</span>

            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: EASE, delay: 1.1 }}
              className="absolute -bottom-10 -left-4 hidden border border-line bg-panel/95 p-4 backdrop-blur md:block"
              data-testid="hero-terminal"
            >
              <div className="mb-3 text-[10px] tracking-[0.3em] text-gold">SYS.CHECK</div>
              <div className="space-y-1.5">
                {t.hero.term.map((row) => (
                  <div key={row.k} className="flex items-center gap-4 text-[10px] tracking-[0.15em]">
                    <span className="w-24 text-zinc-500">{row.k}</span>
                    <span className={row.hot ? "font-bold text-gold" : "text-zinc-200"}>{row.v}</span>
                  </div>
                ))}
              </div>
              <div className="mt-3 flex items-center gap-2 text-[10px] text-zinc-500">
                <span>&gt;</span>
                <span className="inline-block h-3 w-2 bg-gold blink" />
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6, duration: 0.8 }} className="pointer-events-none absolute bottom-6 left-1/2 hidden -translate-x-1/2 items-center gap-2 text-[10px] tracking-[0.3em] text-zinc-500 md:flex">
        {t.hero.scroll}
        <ArrowDown size={12} className="animate-bounce text-gold" />
      </motion.div>
    </section>
  );
}
